import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { BarChart3, Plus, Edit, Trash2, AlertCircle, Save, X } from 'lucide-react'

interface Statistic {
  id: string
  label: string
  value: string
  icon: string
  description: string | null
  created_at: string
  updated_at: string
}

const iconOptions = [
  'users', 'home', 'graduation', 'book', 'heart', 'trending',
  'building', 'car', 'tree', 'sun', 'moon', 'star'
]

export function StatisticsManager() {
  const [statistics, setStatistics] = useState<Statistic[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingStatistic, setEditingStatistic] = useState<Statistic | null>(null)
  const [formData, setFormData] = useState({
    label: '',
    value: '',
    icon: 'users',
    description: ''
  })

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase
        .from('statistics')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setStatistics(data || [])
    } catch (err) {
      setError('Failed to fetch statistics')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')

    try {
      if (editingStatistic) {
        // Update existing
        const { error } = await supabase
          .from('statistics')
          .update({
            ...formData,
            description: formData.description || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingStatistic.id)

        if (error) throw error
      } else {
        // Create new
        const { error } = await supabase
          .from('statistics')
          .insert({
            ...formData,
            description: formData.description || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (error) throw error
      }

      await fetchStatistics()
      handleCloseDialog()
    } catch (err) {
      setError('Failed to save statistic')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('statistics')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchStatistics()
    } catch (err) {
      setError('Failed to delete statistic')
    }
  }

  const handleEdit = (statistic: Statistic) => {
    setEditingStatistic(statistic)
    setFormData({
      label: statistic.label,
      value: statistic.value,
      icon: statistic.icon,
      description: statistic.description || ''
    })
    setIsDialogOpen(true)
  }

  const handleAddNew = () => {
    setEditingStatistic(null)
    setFormData({
      label: '',
      value: '',
      icon: 'users',
      description: ''
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingStatistic(null)
    setFormData({
      label: '',
      value: '',
      icon: 'users',
      description: ''
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading statistics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
            Statistics Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage village statistics and demographic data
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Statistic
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 border-b border-slate-200">
          <CardTitle className="flex items-center gap-3 text-xl font-semibold text-slate-800">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            Statistics ({statistics.length})
          </CardTitle>
          <CardDescription className="text-slate-600">
            Village demographic and statistical information
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {statistics.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No statistics found</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Statistic
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Icon</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statistics.map((statistic) => (
                  <TableRow key={statistic.id}>
                    <TableCell className="font-medium">{statistic.label}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-lg">
                        {statistic.value}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{statistic.icon}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {statistic.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(statistic)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete statistic?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. The selected statistic will be removed from the portal.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
                                onClick={() => handleDelete(statistic.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-white/95 backdrop-blur-sm">
          <DialogHeader className="bg-gradient-to-r from-purple-50 to-violet-50 border-b border-slate-200 p-6 -m-6 mb-6">
            <DialogTitle className="text-xl font-semibold text-slate-800">
              {editingStatistic ? 'Edit Statistic' : 'Add New Statistic'}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {editingStatistic ? 'Update statistic information' : 'Add a new village statistic'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label htmlFor="label" className="text-sm font-medium text-slate-700">Label</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., Total Population"
                  className="border-slate-200 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="value" className="text-sm font-medium text-slate-700">Value</Label>
                <Input
                  id="value"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="e.g., 3,245"
                  className="border-slate-200 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="icon" className="text-sm font-medium text-slate-700">Icon</Label>
              <select
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 bg-background rounded-md focus:border-purple-500 focus:ring-purple-500"
              >
                {iconOptions.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of this statistic"
                className="border-slate-200 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
              <Button variant="outline" onClick={handleCloseDialog} className="border-slate-200 hover:bg-slate-50">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
